/**
 * performanceService – every read of quiz results for the analytics screens.
 *
 * Each query is bounded (a date range, a limit, or both) and constrained on the
 * fields firestore.rules checks — student_id, class_id, school_id — so the
 * rules can prove a query is allowed and nobody downloads a whole school to
 * draw one chart. Results come back normalised (lib/analytics/metrics.ts), so
 * old and server-graded records read the same way.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  limit as limitTo,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEFAULT_PASS_THRESHOLD, normaliseScore, toMillis, type DateRange, type RawScore, type Result } from '../lib/analytics/metrics';

const SCORES = 'student_scores';
/** Ceiling on any one read. A school needing more than this should be using a date range. */
export const MAX_RESULTS = 2000;

const toResults = (docs: { id: string; data: () => unknown }[]): Result[] =>
  docs.map((d) => normaliseScore({ id: d.id, ...(d.data() as RawScore) }));

function rangeConstraints(range?: DateRange | null): QueryConstraint[] {
  const out: QueryConstraint[] = [];
  if (range?.from != null) out.push(where('completed_at', '>=', Timestamp.fromMillis(range.from)));
  if (range?.to != null) out.push(where('completed_at', '<', Timestamp.fromMillis(range.to)));
  return out;
}

/** A school's pass mark, or the default when it has not set one. */
export async function getPassThreshold(schoolId: string | null | undefined): Promise<number> {
  if (!schoolId) return DEFAULT_PASS_THRESHOLD;
  try {
    const snap = await getDoc(doc(db, 'schools', schoolId));
    const value = snap.exists() ? snap.data()?.settings?.pass_threshold : undefined;
    return typeof value === 'number' && value >= 0 && value <= 100 ? value : DEFAULT_PASS_THRESHOLD;
  } catch {
    return DEFAULT_PASS_THRESHOLD;
  }
}

/**
 * Set a school's pass mark. The rules allow the school's admin, its principal
 * and platform admins; everyone else is refused.
 */
export async function setPassThreshold(schoolId: string, value: number): Promise<void> {
  if (!Number.isFinite(value) || value < 0 || value > 100) throw new Error('The pass mark must be between 0 and 100.');
  await updateDoc(doc(db, 'schools', schoolId), {
    'settings.pass_threshold': Math.round(value),
    updatedAt: new Date().toISOString(),
  });
}

/** One result, for the result detail page. Null when missing or not readable. */
export async function getResult(scoreId: string): Promise<Result | null> {
  const snap = await getDoc(doc(db, SCORES, scoreId));
  return snap.exists() ? normaliseScore({ id: snap.id, ...(snap.data() as RawScore) }) : null;
}

/**
 * Live results for one class session.
 *
 * Filters on class_id and school_id as well as session_id: they are always
 * equal to the session's, and without them the rules cannot tell that a
 * teacher's query only touches their own class.
 */
export function subscribeSessionResults(
  session: { id: string; class_id?: string | null; school_id?: string | null },
  onData: (results: Result[]) => void,
  onError: (error: Error) => void
): () => void {
  const constraints: QueryConstraint[] = [where('session_id', '==', session.id)];
  if (session.school_id) constraints.push(where('school_id', '==', session.school_id));
  if (session.class_id) constraints.push(where('class_id', '==', session.class_id));
  return onSnapshot(
    query(collection(db, SCORES), ...constraints, limitTo(500)),
    (snap) => onData(toResults(snap.docs)),
    onError
  );
}

/**
 * One student's history, newest first.
 *
 * Staff pass the student's school: the rules let a teacher or school read a
 * student's scores by checking that school, and can only prove the query is
 * allowed when it is constrained to it. A student reading their own omits it.
 */
export async function getStudentResults(studentId: string, schoolId?: string | null, max = 500): Promise<Result[]> {
  const snap = await getDocs(
    query(
      collection(db, SCORES),
      where('student_id', '==', studentId),
      ...(schoolId ? [where('school_id', '==', schoolId)] : []),
      orderBy('completed_at', 'desc'),
      limitTo(max)
    )
  );
  return toResults(snap.docs);
}

export interface LaunchRow {
  id: string;
  status: string | null;
  durationSeconds: number | null;
  launchedAt: number | null;
}

/** A student's lesson launches, for learning time. Same school rule as above. */
export async function getStudentLaunches(studentId: string, schoolId?: string | null, max = 500): Promise<LaunchRow[]> {
  const snap = await getDocs(
    query(
      collection(db, 'lesson_launches'),
      where('student_id', '==', studentId),
      ...(schoolId ? [where('school_id', '==', schoolId)] : []),
      limitTo(max)
    )
  );
  return snap.docs.map((d) => launchRow(d.id, d.data()));
}

export function launchRow(id: string, data: Record<string, any>): LaunchRow {
  return {
    id,
    status: data.completion_status ?? null,
    durationSeconds: typeof data.duration_seconds === 'number' && data.duration_seconds >= 0 ? data.duration_seconds : null,
    launchedAt: toMillis(data.launched_at),
  };
}

/** Every result in one class, newest first. Needs the class's school for the rules. */
export async function getClassResults(classId: string, schoolId: string, range?: DateRange | null, max = MAX_RESULTS): Promise<Result[]> {
  const snap = await getDocs(
    query(
      collection(db, SCORES),
      where('class_id', '==', classId),
      where('school_id', '==', schoolId),
      ...rangeConstraints(range),
      orderBy('completed_at', 'desc'),
      limitTo(max)
    )
  );
  return toResults(snap.docs);
}

/** Every result in a school, newest first. For school admins, principals and admins. */
export async function getSchoolResults(schoolId: string, range?: DateRange | null, max = MAX_RESULTS): Promise<Result[]> {
  const snap = await getDocs(
    query(collection(db, SCORES), where('school_id', '==', schoolId), ...rangeConstraints(range), orderBy('completed_at', 'desc'), limitTo(max))
  );
  return toResults(snap.docs);
}

export interface SessionSummaryRow {
  id: string;
  classId: string | null;
  teacherId: string | null;
  lessonTitle: string | null;
  status: string;
  startedAt: number | null;
  endedAt: number | null;
}

function sessionRow(id: string, s: Record<string, any>): SessionSummaryRow {
  return {
    id,
    classId: s.class_id ?? null,
    teacherId: s.teacher_uid ?? null,
    lessonTitle: s.launched_lesson?.title ?? null,
    status: s.status ?? 'ended',
    startedAt: toMillis(s.created_at),
    endedAt: toMillis(s.ended_at),
  };
}

/** A teacher's most recent class sessions, newest first. Uses the (teacher_uid, updated_at) index. */
export async function getRecentTeacherSessions(teacherUid: string, max = 10): Promise<SessionSummaryRow[]> {
  const snap = await getDocs(
    query(collection(db, 'class_sessions'), where('teacher_uid', '==', teacherUid), orderBy('updated_at', 'desc'), limitTo(max))
  );
  return snap.docs.map((d) => sessionRow(d.id, d.data()));
}

/**
 * Every result for one lesson, within classes or a school.
 *
 * A teacher passes their class ids: the rules check each result's class, so
 * the query has to name them (`in` takes up to 30, so larger lists are
 * chunked). A school passes only its id.
 */
export async function getLessonResults(opts: {
  chapterId: string;
  topicId: string;
  schoolId: string;
  classIds?: string[] | null;
}): Promise<Result[]> {
  const base = [
    where('school_id', '==', opts.schoolId),
    where('chapter_id', '==', opts.chapterId),
    where('topic_id', '==', opts.topicId),
  ];
  if (!opts.classIds) {
    const snap = await getDocs(query(collection(db, SCORES), ...base, limitTo(MAX_RESULTS)));
    return toResults(snap.docs);
  }
  const chunks: string[][] = [];
  for (let i = 0; i < opts.classIds.length; i += 30) chunks.push(opts.classIds.slice(i, i + 30));
  const snaps = await Promise.all(
    chunks.map((ids) => getDocs(query(collection(db, SCORES), ...base, where('class_id', 'in', ids), limitTo(MAX_RESULTS))))
  );
  return snaps.flatMap((snap) => toResults(snap.docs));
}

export interface RosterStudent {
  uid: string;
  name: string;
  email: string | null;
  classIds: string[];
}

/** Students enrolled in any of these classes, the way the teacher dashboard reads them. */
export async function getClassStudents(classIds: string[], schoolId: string): Promise<RosterStudent[]> {
  const chunks: string[][] = [];
  for (let i = 0; i < classIds.length; i += 30) chunks.push(classIds.slice(i, i + 30));
  const snaps = await Promise.all(
    chunks.map((ids) =>
      getDocs(
        query(
          collection(db, 'users'),
          where('role', '==', 'student'),
          where('school_id', '==', schoolId),
          where('class_ids', 'array-contains-any', ids)
        )
      )
    )
  );
  const byUid = new Map<string, RosterStudent>();
  for (const snap of snaps) {
    for (const d of snap.docs) {
      const u = d.data();
      byUid.set(d.id, {
        uid: d.id,
        name: u.name || u.displayName || u.display_name || u.email || 'Student',
        email: u.email ?? null,
        classIds: Array.isArray(u.class_ids) ? u.class_ids : [],
      });
    }
  }
  return [...byUid.values()];
}
